import type { Dispatch } from 'vuex/types';
import type { ThisTypedComponentOptionsWithRecordProps } from 'vue/types/options';
import type { Vue } from 'vue/types/vue';

import { toNonEmptyStringArray } from '@/helpers/convert';
import preventUnload from '@/helpers/preventUnload';

import BaseForm from '@/components/forms/BaseForm.vue';

const form: ThisTypedComponentOptionsWithRecordProps<
  Vue,
  FormMixinData,
  FormMixinMethods,
  FormMixinComputed,
  FormMixinProps
> = {
  components: { BaseForm },
  props: {
    confirmDiscard: {
      type: Boolean,
      default: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    verb: {
      type: String,
      default: 'Submit',
    },
  },
  data() {
    return {
      confirmDiscardRemoveRouteHook: null,
      confirmDiscardUnloadListener: null,
      errorMessageData: null,
      pendingActions: 0,
      successMessageData: null,
      warningMessageData: null,
    };
  },
  computed: {
    errorMessage: {
      get() {
        return toNonEmptyStringArray(this.errorMessageData);
      },
      set(value: any) {
        this.errorMessageData = value;
      },
    },
    forward() {
      return {
        ...this.$attrs,
        errorMessage: this.errorMessage,
        loading: this.isLoading,
        successMessage: this.successMessage,
        verb: this.verb,
        warningMessage: this.warningMessage,
      };
    },
    isDirty() {
      return false;
    },
    isLoading() {
      return !!this.loading || this.pendingActions > 0;
    },
    successMessage: {
      get() {
        return toNonEmptyStringArray(this.successMessageData);
      },
      set(value: any) {
        this.successMessageData = value;
      },
    },
    warningMessage: {
      get() {
        return toNonEmptyStringArray(this.warningMessageData);
      },
      set(value: any) {
        this.warningMessageData = value;
      },
    },
  },
  methods: {
    action,
    canDiscardChanges() {
      let message: string;
      if (!this.confirmDiscard || !this.isDirty || !(message = this.getConfirmDiscardMessage())) {
        return true;
      }
      return confirm(message);
    },
    clearMessages() {
      this.errorMessageData = null;
      this.successMessageData = null;
      this.warningMessageData = null;
    },
    disableConfirmDiscard() {
      const removeHook = this.confirmDiscardRemoveRouteHook;
      if (typeof removeHook === 'function') {
        removeHook();
      }
      this.confirmDiscardRemoveRouteHook = null;
      const unloadListener = this.confirmDiscardUnloadListener;
      if (typeof unloadListener === 'function') {
        window.removeEventListener('beforeunload', unloadListener, false);
      }
      this.confirmDiscardUnloadListener = null;
    },
    enableConfirmDiscard() {
      if (!this.confirmDiscard) {
        return;
      }
      if (!this.confirmDiscardRemoveRouteHook) {
        this.confirmDiscardRemoveRouteHook = this.$router.beforeEach((to, from, next) => {
          if (this.canDiscardChanges()) {
            next();
          } else {
            next(false);
          }
        });
      }
      if (!this.confirmDiscardUnloadListener) {
        const listener = onBeforeUnload.bind(this);
        this.confirmDiscardUnloadListener = listener;
        window.addEventListener('beforeunload', listener, false);
      }
    },
    getConfirmDiscardMessage() {
      return 'You have unsaved changes that may be lost if you continue. Are you sure?';
    },
    getDefaultErrorMessage() {
      return 'There was an error processing your request. Please try again later.';
    },
    getFormElement() {
      return getFormElementRecursive(this);
    },
    handleApiError(error, resolve, statusCode, messages) {
      this.errorMessageData = messages;
    },
    handleError(error) {
      let resolved = false;
      let resolvedValue: any;
      const resolve = (value: any) => {
        resolved = true;
        resolvedValue = value;
      };

      if (isApiError(error)) {
        const messages: string[] = [];
        const response = error.response;

        let statusCode = Number(response.status) || null;

        const data = response.data;
        if (data) {
          const set = new Set<string>();
          for (const message of toNonEmptyStringArray(data.message).filter(isUsefulErrorMessage)) {
            const key = message.toLowerCase();
            if (!set.has(key)) {
              messages.push(message);
              set.add(key);
            }
          }
          if (!statusCode) {
            statusCode = Number(data.statusCode);
          }
        }

        if (
          typeof statusCode !== 'number' ||
          !Number.isInteger(statusCode) ||
          statusCode < 400 ||
          statusCode > 599
        ) {
          statusCode = null;
        }

        this.handleApiError(error, resolve, statusCode, messages);
      } else {
        this.handleUnknownError(error, resolve);
      }

      return resolved ? resolvedValue : Promise.reject(error);
    },
    handleUnknownError() {
      this.errorMessageData = this.getDefaultErrorMessage();
    },
    reportValidity() {
      const form = this.getFormElement();
      return form ? form.reportValidity() : true;
    },
    requestSubmit(submitter) {
      if (submitter instanceof Event) {
        submitter = null;
      }
      const form = this.getFormElement();
      if (form) {
        requestSubmitNative(form, submitter);
      }
    },
    submit() {
      const form = this.getFormElement();
      if (form) {
        form.submit();
      }
    },
  },
  watch: {
    confirmDiscard(value: boolean | Empty) {
      value ? this.enableConfirmDiscard() : this.disableConfirmDiscard();
    },
    isDirty(value: boolean) {
      this.$emit(value ? 'dirty' : 'clean');
    },
    isLoading: {
      handler(value: unknown) {
        this.$emit('update:loading', !!value);
      },
      immediate: true,
    },
  },
  activated() {
    this.enableConfirmDiscard();
  },
  beforeDestroy() {
    this.disableConfirmDiscard();
  },
  deactivated() {
    this.disableConfirmDiscard();
  },
  mounted() {
    this.enableConfirmDiscard();
  },
};

export default form;

export interface FormComponent extends Vue {
  /**
   * __(Prop)__
   *
   * Whether to ask the user for confirmation when changing routes or leaving the page
   * while the form has unsaved changes.
   */
  readonly confirmDiscard: boolean | Empty;
  /**
   * __(Prop)__
   *
   * Whether the form is loading.
   */
  readonly loading: boolean | Empty;
  /**
   * __(Prop)__
   *
   * The text within the form's submit button.
   */
  readonly verb: string | Empty;

  /**
   * __(Computed)__
   *
   * An array of strings representing the error message(s) shown by the form.
   *
   * Setting this property has the same effect as setting the `errorMessageData` property.
   */
  errorMessage: string[];
  /**
   * __(Computed)__
   *
   * The attributes which are forwarded to the base form.
   */
  readonly forward: Record<string, any>;
  /**
   * __(Computed)__
   *
   * Unless overridden in a derived component, returns `true` if the `pendingActions`
   * data property is greater than 0 or `false` otherwise.
   */
  readonly isLoading: boolean;
  /**
   * __(Computed)__
   *
   * `true` if the form has unsaved changes; otherwise, `false`.
   *
   * Unless overridden in a derived mixin or component, this property always returns
   * `false`.
   */
  readonly isDirty: boolean;
  /**
   * __(Computed)__
   *
   * An array of strings representing the success message(s) shown by the form.
   *
   * Setting this property has the same effect as setting the `successMessageData`
   * property.
   */
  successMessage: string[];
  /**
   * __(Computed)__
   *
   * An array of strings representing the warning message(s) shown by the form.
   *
   * Setting this property has the same effect as setting the `warningMessageData`
   * property.
   */
  warningMessage: string[];

  /**
   * __(Data)__
   *
   * A function which, when invoked, removes the form's router navigation hook used for
   * discard confirmation.
   */
  confirmDiscardRemoveRouteHook: Function | null;
  /**
   * __(Data)__
   *
   * The event listener currently bound to the window's `"beforeunload"` event for discard confirmation.
   */
  confirmDiscardUnloadListener: ((event: Event) => void) | null;
  /**
   * __(Data)__
   *
   * The raw error message data. Use the `errorMessage` computed property to retrieve
   * the error message data as an array of strings.
   */
  errorMessageData: any;
  /**
   * __(Data)__
   *
   * The number of actions dispatched by the form whose promises have not yet been
   * settled.
   */
  pendingActions: number;
  /**
   * __(Data)__
   *
   * The raw success message data. Use the `successMessage` computed property to
   * retrieve the success message data as an array of strings.
   */
  successMessageData: any;
  /**
   * __(Data)__
   *
   * The raw warning message data. Use the `warningMessage` computed property to
   * retrieve the warning message data as an array of strings.
   */
  warningMessageData: any;

  /**
   * __(Method)__
   *
   * Dispatches an action via the Vuex store and returns the result, intercepting any API
   * errors and populating the `errorMessages` data member as appropriate. This method
   * has the same argument signature and return value as `this.$store.dispatch()`.
   */
  action: Dispatch;
  /**
   * __(Method)__
   *
   * Returns `true` if any unsaved changes made to the `user` or `profile` may be
   * discarded; otherwise, returns `false`.
   */
  canDiscardChanges(): boolean;
  /**
   * __(Method)__
   *
   * Sets the `errorMessage`, `successMessage`, and `warningMessage` properties to
   * `null`.
   */
  clearMessages(): void;
  /**
   * __(Method)__
   *
   * Removes the event listeners and route hooks necessary for discard confirmation.
   */
  disableConfirmDiscard(): void;
  /**
   * __(Method)__
   *
   * Adds the event listeners and route hooks necessary for discard confirmation.
   */
  enableConfirmDiscard(): void;
  /**
   * __(Method)__
   *
   * Returns the message shown when prompting the user for confirmation to discard
   * unsaved changes.
   */
  getConfirmDiscardMessage(): string;
  /**
   * __(Method)__
   *
   * Returns the default message shown when an unknown error has occurred.
   */
  getDefaultErrorMessage(): string;
  /**
   * Returns the root `<form>` element, or `null` if no `<form>` element is found.
   */
  getFormElement(): HTMLFormElement | null;
  /**
   * __(Method)__
   *
   * Invoked when an API error occurrs within an action dispatched by the `action()`
   * method.
   *
   * @param statusCode
   * The HTTP response status code.
   *
   * @param error
   * The `AxiosError` object associated with the response.
   *
   * @param messages
   * An array containing any potentially useful error messages that have already been
   * inferred from the `AxiosError` so far.
   *
   * @param resolve
   * A method which, when invoked, resolves the pending promise with the provided value. If
   * this method is not invoked, then the pending promise is rejected with the original error.
   */
  handleApiError(
    error: ApiError,
    resolve: (value: any) => void,
    statusCode: number | null,
    messages: readonly string[]
  ): void;
  /**
   * Invokes either the {@link FormComponent.handleApiError `handleApiError`} method or the
   * {@link FormComponent.handleUnknownError `handleUnknownError`} method, depending on the
   * nature of the provided error.
   *
   * @param error
   * The error to handle.
   */
  handleError(error: any): unknown;
  /**
   * Invoked when an unknown error occurrs within an action dispatched by the `action()`
   * method.
   *
   * @param rejectionValue
   * The value with which the pending promise was rejected.
   *
   * @param resolve
   * A method which, when invoked, resolves the pending promise with the provided value. If
   * this method is not invoked, then the pending promise is rejected with the original error.
   */
  handleUnknownError(rejectionValue: any, resolve: (value: any) => void): void;
  /**
   * Returns `true` if the form is valid, or `false` if at least one field is invalid. If `false` is
   * returned, then this method may render field errors in the UI.
   */
  reportValidity(): boolean;
  /**
   * Attempts to submit the form after validating it. If validation succeeds or is not needed,
   * then a `"submit"` event is emitted.
   *
   * This method emulates the native `HTMLFormElement.prototype.requestSubmit()` method
   * (https://developer.mozilla.org/en-US/docs/Web/API/HTMLFormElement/requestSubmit).
   *
   * @param submitter
   * The submit button whose attributes describe the method by which the form is to be
   * submitted. This may be either an `<input>` or `<button>` element whose `type`
   * attribute is `"submit"`. If omitted, then the form element itself is used as the submitter.
   */
  requestSubmit(submitter?: Submitter | null): void;
  /**
   * Attempts to submit the form **without** validating it and **without** emitting a `"submit"`
   * event.
   *
   * This method emulates the native `HTMLFormElement.prototype.submit()` method
   * (https://developer.mozilla.org/en-US/docs/Web/API/HTMLFormElement/submit).
   */
  submit(): void;
}

export type FormMixinComputed = Pick<
  FormComponent,
  'errorMessage' | 'forward' | 'isDirty' | 'isLoading' | 'successMessage' | 'warningMessage'
>;

export type FormMixinData = Pick<
  FormComponent,
  | 'confirmDiscardRemoveRouteHook'
  | 'confirmDiscardUnloadListener'
  | 'errorMessageData'
  | 'pendingActions'
  | 'successMessageData'
  | 'warningMessageData'
>;

export type FormMixinMethods = Pick<
  FormComponent,
  | 'action'
  | 'canDiscardChanges'
  | 'clearMessages'
  | 'disableConfirmDiscard'
  | 'enableConfirmDiscard'
  | 'getConfirmDiscardMessage'
  | 'getDefaultErrorMessage'
  | 'getFormElement'
  | 'handleApiError'
  | 'handleError'
  | 'handleUnknownError'
  | 'reportValidity'
  | 'requestSubmit'
  | 'submit'
>;

export type FormMixinProps = Pick<FormComponent, 'confirmDiscard' | 'loading' | 'verb'>;

const axiosDefaultErrorMessage = /^request failed with status code/i;

async function action(this: FormComponent, ...args: any[]): Promise<any> {
  this.pendingActions++;
  try {
    return await (this.$store.dispatch as (...args: any[]) => Promise<any>)(...args);
  } catch (error) {
    return await this.handleError(error);
  } finally {
    this.pendingActions = Math.max(0, this.pendingActions - 1);
  }
}

function getFormElementRecursive(component: Vue): HTMLFormElement | null {
  let node: HTMLFormElement | Comment | null | undefined = component.$el as any;
  if (!node) {
    return null;
  }
  if (node instanceof HTMLElement) {
    return node.localName === 'form' ? node : node.querySelector('form');
  }
  if (node instanceof Comment) {
    for (const child of component.$children) {
      if ((node = getFormElementRecursive(child))) {
        return node;
      }
    }
  }
  return null;
}

function isApiError(value: any): value is ApiError {
  return !!(value && typeof value === 'object' && value.isAxiosError && value.response);
}

function isUsefulErrorMessage(message: string | null | undefined): boolean {
  return message ? !axiosDefaultErrorMessage.test(message) : false;
}

function onBeforeUnload(this: FormComponent, ...args: Parameters<typeof preventUnload>) {
  if (this.confirmDiscard && this.isDirty) {
    return preventUnload(...args);
  }
}

type Submitter = HTMLButtonElement | HTMLInputElement;

function getSubmitButton(
  form: HTMLFormElement,
  submitter: Submitter | null | undefined
): { button: Submitter; detach: boolean } {
  let button: Submitter | null =
    submitter ||
    form.querySelector('button[type="submit"]:not(:disabled), input[type="submit"]:not(:disabled)');
  let detach = false;
  if (!button) {
    button = document.createElement('button');
    button.setAttribute(
      'style',
      'position: absolute; width: 1px; height: 1px; margin: -1px; padding: 0; overflow: hidden; clip: rect(0,0,0,0); border: 0;'
    );
    button.type = 'submit';
    form.appendChild(button);
    detach = true;
  }
  return { button, detach };
}

const requestSubmitNative = (function (): (
  form: HTMLFormElement,
  submitter?: Submitter | null
) => void {
  const protoMethod = HTMLFormElement.prototype.requestSubmit;
  if (typeof protoMethod === 'function') {
    return function requestSubmit(form, submitter) {
      if (!(form instanceof HTMLFormElement)) {
        return;
      }
      const { button, detach } = getSubmitButton(form, submitter);
      try {
        return protoMethod.call(form, button);
      } finally {
        let parent: Node | null;
        if (detach && (parent = button.parentNode)) {
          parent.removeChild(button);
        }
      }
    };
  }
  return function requestSubmit(form, submitter) {
    if (!(form instanceof HTMLFormElement)) {
      return;
    }
    const { button, detach } = getSubmitButton(form, submitter);
    try {
      button.click();
    } finally {
      let parent: Node | null;
      if (detach && (parent = button.parentNode)) {
        parent.removeChild(button);
      }
    }
  };
})();
